/**
 * Cache Manager - Efficient caching strategies for Shopify theme
 * Implements multiple cache layers for optimal performance
 */

class CacheManager {
  constructor() {
    this.memoryCache = new Map();
    this.localStoragePrefix = 'voodoo_cache_';
    this.sessionStoragePrefix = 'voodoo_session_';
    this.maxMemorySize = 50; // Maximum items in memory cache
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
  }

  // Memory cache (fastest, session-only)
  setMemory(key, value, ttl = this.defaultTTL) {
    // Implement LRU eviction
    if (this.memoryCache.size >= this.maxMemorySize) {
      const firstKey = this.memoryCache.keys().next().value;
      this.memoryCache.delete(firstKey);
    }

    this.memoryCache.set(key, {
      value,
      expires: Date.now() + ttl
    });
  }

  getMemory(key) {
    const item = this.memoryCache.get(key);
    if (!item) return null;

    if (Date.now() > item.expires) {
      this.memoryCache.delete(key);
      return null;
    }

    // Move to end (LRU)
    this.memoryCache.delete(key);
    this.memoryCache.set(key, item);
    return item.value;
  }

  // LocalStorage cache (persistent across sessions)
  setLocal(key, value, ttl = this.defaultTTL) {
    try {
      const data = {
        value,
        expires: Date.now() + ttl
      };
      localStorage.setItem(this.localStoragePrefix + key, JSON.stringify(data));
    } catch (e) {
      console.warn('LocalStorage unavailable:', e);
    }
  }

  getLocal(key) {
    try {
      const item = localStorage.getItem(this.localStoragePrefix + key);
      if (!item) return null;

      const data = JSON.parse(item);
      if (Date.now() > data.expires) {
        localStorage.removeItem(this.localStoragePrefix + key);
        return null;
      }

      return data.value;
    } catch (e) {
      console.warn('LocalStorage read error:', e);
      return null;
    }
  }

  // SessionStorage cache (tab-specific)
  setSession(key, value) {
    try {
      sessionStorage.setItem(this.sessionStoragePrefix + key, JSON.stringify(value));
    } catch (e) {
      console.warn('SessionStorage unavailable:', e);
    }
  }

  getSession(key) {
    try {
      const item = sessionStorage.getItem(this.sessionStoragePrefix + key);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      console.warn('SessionStorage read error:', e);
      return null;
    }
  }

  // Intelligent cache strategy
  set(key, value, options = {}) {
    const {
      strategy = 'auto',
      ttl = this.defaultTTL,
      persistent = false
    } = options;

    // Auto strategy selection
    let useStrategy = strategy;
    if (strategy === 'auto') {
      if (persistent) {
        useStrategy = 'local';
      } else if (typeof value === 'object' && JSON.stringify(value).length > 1000) {
        useStrategy = 'local';
      } else {
        useStrategy = 'memory';
      }
    }

    switch (useStrategy) {
      case 'memory':
        this.setMemory(key, value, ttl);
        break;
      case 'local':
        this.setLocal(key, value, ttl);
        break;
      case 'session':
        this.setSession(key, value);
        break;
    }
  }

  get(key, strategies = ['memory', 'session', 'local']) {
    for (const strategy of strategies) {
      let value;
      switch (strategy) {
        case 'memory':
          value = this.getMemory(key);
          break;
        case 'local':
          value = this.getLocal(key);
          break;
        case 'session':
          value = this.getSession(key);
          break;
      }

      if (value !== null) {
        // Promote to memory cache if found elsewhere
        if (strategy !== 'memory') {
          this.setMemory(key, value);
        }
        return value;
      }
    }

    return null;
  }

  // Clear expired entries
  cleanup() {
    // Memory cache cleanup
    for (const [key, item] of this.memoryCache.entries()) {
      if (Date.now() > item.expires) {
        this.memoryCache.delete(key);
      }
    }

    // LocalStorage cleanup
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.localStoragePrefix)) {
          const item = localStorage.getItem(key);
          try {
            const data = JSON.parse(item);
            if (Date.now() > data.expires) {
              localStorage.removeItem(key);
            }
          } catch (e) {
            // Invalid format, remove
            localStorage.removeItem(key);
          }
        }
      }
    } catch (e) {
      console.warn('LocalStorage cleanup error:', e);
    }
  }

  // Clear all cache
  clear() {
    this.memoryCache.clear();
    
    try {
      // Clear localStorage items
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.localStoragePrefix)) {
          localStorage.removeItem(key);
        }
      }

      // Clear sessionStorage items
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.sessionStoragePrefix)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.warn('Cache clear error:', e);
    }
  }

  // Get cache statistics
  getStats() {
    let localCount = 0;
    let sessionCount = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.localStoragePrefix)) {
          localCount++;
        }
      }

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.sessionStoragePrefix)) {
          sessionCount++;
        }
      }
    } catch (e) {
      console.warn('Cache stats error:', e);
    }

    return {
      memory: this.memoryCache.size,
      local: localCount,
      session: sessionCount
    };
  }
}

// Product cache specifically optimized for Shopify
class ProductCache extends CacheManager {
  constructor() {
    super();
    this.productTTL = 10 * 60 * 1000; // 10 minutes for products
    this.variantTTL = 15 * 60 * 1000; // 15 minutes for variants
  }

  cacheProduct(productId, productData) {
    this.set(`product_${productId}`, productData, {
      ttl: this.productTTL,
      persistent: true
    });
  }

  getProduct(productId) {
    return this.get(`product_${productId}`);
  }

  cacheVariants(productId, variants) {
    this.set(`variants_${productId}`, variants, {
      ttl: this.variantTTL,
      persistent: true
    });
  }

  getVariants(productId) {
    return this.get(`variants_${productId}`);
  }

  cacheProductForm(productId, formData) {
    this.set(`form_${productId}`, formData, {
      strategy: 'session'
    });
  }

  getProductForm(productId) {
    return this.get(`form_${productId}`, ['session']);
  }
}

// Collection cache
class CollectionCache extends CacheManager {
  constructor() {
    super();
    this.collectionTTL = 15 * 60 * 1000; // 15 minutes
  }

  cacheCollection(collectionId, data) {
    this.set(`collection_${collectionId}`, data, {
      ttl: this.collectionTTL,
      persistent: true
    });
  }

  getCollection(collectionId) {
    return this.get(`collection_${collectionId}`);
  }

  cacheFilters(collectionId, filters) {
    this.set(`filters_${collectionId}`, filters, {
      ttl: this.collectionTTL,
      strategy: 'session'
    });
  }

  getFilters(collectionId) {
    return this.get(`filters_${collectionId}`, ['session']);
  }
}

// Initialize global cache instances
if (typeof window !== 'undefined') {
  window.cacheManager = new CacheManager();
  window.productCache = new ProductCache();
  window.collectionCache = new CollectionCache();

  // Cleanup expired cache entries every 5 minutes
  setInterval(() => {
    window.cacheManager.cleanup();
    window.productCache.cleanup();
    window.collectionCache.cleanup();
  }, 5 * 60 * 1000);

  // Clear cache on theme updates (check theme version)
  const currentThemeVersion = '1.0.0'; // Update this when theme changes
  const cachedVersion = window.cacheManager.getLocal('theme_version');
  if (cachedVersion !== currentThemeVersion) {
    window.cacheManager.clear();
    window.productCache.clear();
    window.collectionCache.clear();
    window.cacheManager.setLocal('theme_version', currentThemeVersion, Infinity);
  }
} 